import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress
} from '@mui/material'
import { Assignment, AccessTime, TrendingUp, People } from '@mui/icons-material'
import { useAuth } from '@shared/auth/AuthContext'
import { ticketService } from '@shared/services/ApiClient'
import { colors } from '@shared/styles/theme'

function AgentDashboard() {
  const { user } = useAuth()
  const [apiStatus, setApiStatus] = useState<'loading' | 'success' | 'error' | 'idle'>('idle')
  const [agentStats, setAgentStats] = useState({
    assigned: 0,
    inProgress: 0,
    resolved: 0,
    total: 0
  })

  useEffect(() => {
    loadAgentStats()
  }, [])

  const loadAgentStats = async () => {
    setApiStatus('loading')
    try {
      const assignedTickets = await ticketService.getAssignedTickets(1, 100)
      const inProgress = assignedTickets.data.filter((t: any) => t.status === 'In Progress').length
      const resolved = assignedTickets.data.filter((t: any) => t.status === 'Resolved').length

      setAgentStats({
        assigned: assignedTickets.totalCount,
        inProgress,
        resolved,
        total: assignedTickets.totalCount
      })
      setApiStatus('success')
    } catch (error) {
      console.error('Failed to load agent stats:', error)
      setApiStatus('error')
    }
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Agent Dashboard
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Welcome back, {user?.firstName || user?.fullName || 'Agent'}! Here's your ticket overview.
      </Typography>

      {/* Agent Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assignment sx={{ fontSize: 40, color: colors.techBlue, mb: 1 }} />
              <Typography variant="h4">
                {apiStatus === 'loading' ? <CircularProgress size={24} /> : agentStats.assigned}
              </Typography>
              <Typography color="text.secondary">Assigned Tickets</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccessTime sx={{ fontSize: 40, color: colors.orange, mb: 1 }} />
              <Typography variant="h4">
                {apiStatus === 'loading' ? <CircularProgress size={24} /> : agentStats.inProgress}
              </Typography>
              <Typography color="text.secondary">In Progress</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 40, color: colors.greenDark, mb: 1 }} />
              <Typography variant="h4">
                {apiStatus === 'loading' ? <CircularProgress size={24} /> : agentStats.resolved}
              </Typography>
              <Typography color="text.secondary">Resolved Today</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <People sx={{ fontSize: 40, color: colors.blue, mb: 1 }} />
              <Typography variant="h4">
                {apiStatus === 'loading' ? <CircularProgress size={24} /> : agentStats.total}
              </Typography>
              <Typography color="text.secondary">Total Managed</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* API Status */}
      {apiStatus === 'error' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Unable to load ticket statistics. You can still access the ticket queue.
        </Alert>
      )}

      {apiStatus === 'success' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Agent dashboard loaded successfully! All systems operational.
        </Alert>
      )}
    </Box>
  )
}

export default AgentDashboard